import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Alert, Box, CircularProgress } from '@mui/material';
import Steps, { seedFromUser } from '../components/Steps';
import Onboarding from '../components/Onboarding';
import AgentService from '../services/agent.service';
import { useAuth } from '../hooks/useAuth';

// #57 - white glove: a principal prepares a contract request FOR one of their
// hierarchy agents (?agent=<id>). The wizard seeds from the TARGET agent's
// prefill; if required fields are missing from that record, the principal is
// routed through the same fill-in screen the agent would have seen, then
// returns here. Authorization is server-side (the prefill 404s outside the
// principal's hierarchy).
const OnBehalfContractRequest = ({ agentId }) => {
    const [prefill, setPrefill] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        setPrefill(null);
        AgentService.prefillFor(agentId)
            .then(setPrefill)
            .catch(() => setError("This agent isn't available to your account."));
    }, [agentId]);
    useEffect(load, [load]);

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (!prefill) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 6 }}>
                <CircularProgress />
            </Box>
        );
    }
    if ((prefill.missingFields || []).length) {
        return (
            <Onboarding
                targetPrefill={prefill}
                targetMissing={prefill.missingFields}
                onSaved={load}
            />
        );
    }
    return (
        <Steps
            initialData={{
                ...seedFromUser({ agent: prefill }),
                onBehalfOfAgent: agentId,
            }}
        />
    );
};

const ContractRequest = () => {
    const { user, refetch } = useAuth();
    const [searchParams] = useSearchParams();
    // #57 - on-behalf mode is principal-only; anyone else's ?agent= is ignored.
    const onBehalfAgentId =
        user?.role === 'agency' ? searchParams.get('agent') : null;

    // Re-pull the user when a request begins so the wizard's new-vs-existing
    // decision (which gates the onboarding DocuSign step) reflects the live
    // server state, not the week-long cached user. Covers entering the flow
    // directly without passing through the dashboard.
    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <Layout>
            <Box sx={{ padding: 4 }}>
                {onBehalfAgentId ? (
                    <OnBehalfContractRequest agentId={onBehalfAgentId} />
                ) : (
                    <Steps />
                )}
            </Box>
        </Layout>
    );
};

export default ContractRequest;
